import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { noteStatusWrite } from './studyNoteBatch';
import { saveStudyNote, type NoteWrite, type StudyNote } from './studyNotesApi';

type Operation = {
  note: StudyNote;
  input: NoteWrite;
  state: 'pending' | 'saved' | 'conflict';
  error?: string;
};

/** Serial, versioned writes. An ambiguous reply keeps its original payload and request id. */
export function StudyNotesBatch({
  owner,
  items,
  terms,
  subjects,
  onOpen,
}: {
  owner: string;
  items: StudyNote[];
  terms: Array<{ id: string; label: string }>;
  subjects: Array<{ subject_key: string; display_name: string }>;
  onOpen: (id: string) => void;
}) {
  const client = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const pending = operations.some((op) => op.state === 'pending');
  const blocker = useBlocker(pending || busy);
  useEffect(() => {
    if (!pending && !busy) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending, busy]);
  async function run(status?: NoteWrite['status']) {
    if (inFlight.current) return;
    inFlight.current = true;
    const plan: Operation[] = status
      ? items
          .filter((note) => selected.includes(note.id) && note.available)
          .map((note) => ({ note, input: noteStatusWrite(note, status), state: 'pending' }))
      : operations;
    setOperations([...plan]);
    setBusy(true);
    for (const op of plan) {
      if (op.state !== 'pending') continue;
      try {
        await saveStudyNote(op.input);
        op.state = 'saved';
        op.error = undefined;
        setSelected((ids) => ids.filter((id) => id !== op.note.id));
        await client.invalidateQueries({ queryKey: ['study-note', owner, op.note.id] });
      } catch (error) {
        op.error =
          error instanceof Error ? error.message : 'Save failed. Retry to check the same request.';
        // A rejected version has not changed the saved note; never overwrite it automatically.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500)
          op.state = 'conflict';
        setOperations([...plan]);
        break;
      }
      setOperations([...plan]);
    }
    setBusy(false);
    inFlight.current = false;
    await client.invalidateQueries({ queryKey: ['study-notes', owner] });
  }
  const toggle = (id: string) =>
    setSelected((ids) => (ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]));
  return (
    <section className="mt-6 space-y-3" aria-label="Saved class notes">
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-surface p-3">
          <label className="flex min-h-11 items-center gap-2 text-sm font-bold">
            <input
              type="checkbox"
              disabled={busy || pending}
              checked={
                items.some((n) => n.available) &&
                items.filter((n) => n.available).every((n) => selected.includes(n.id))
              }
              onChange={(e) =>
                setSelected(
                  e.target.checked ? items.filter((n) => n.available).map((n) => n.id) : [],
                )
              }
            />
            Select loaded notes
          </label>
          <span className="text-sm text-slate2">{selected.length} selected</span>
          <button
            className="btn-pill-secondary"
            disabled={!selected.length || busy || pending}
            onClick={() => void run('archived')}
          >
            Archive selected
          </button>
          <button
            className="btn-pill-ghost"
            disabled={!selected.length || busy || pending}
            onClick={() => void run('active')}
          >
            Restore selected
          </button>
        </div>
      )}
      {operations.length > 0 && (
        <div
          role="status"
          className={`rounded-2xl border border-hairline p-4 text-sm text-ink ${operations.some((op) => op.error) ? 'bg-wash-coral' : 'bg-wash-mint'}`}
        >
          <p>
            {busy
              ? 'Saving notes…'
              : `${operations.filter((op) => op.state === 'saved').length} of ${operations.length} saved.`}
          </p>
          {operations
            .filter((op) => op.error)
            .map((op) => (
              <p key={op.note.id} role="alert" className="mt-2">
                {op.note.title}: {op.error}
                {op.state === 'conflict' && ' Open this note to review the saved version.'}
              </p>
            ))}
          {pending && !busy && (
            <button className="btn-pill-secondary mt-3" onClick={() => void run()}>
              Retry remaining saves
            </button>
          )}
        </div>
      )}
      {blocker.state === 'blocked' && (
        <div role="alert" className="rounded-2xl bg-wash-sunshine p-4 text-ink">
          <p>
            {busy
              ? 'Wait for these saves to finish.'
              : 'Some saves are unfinished. Stay to retry the same requests, or leave and check saved notes later.'}
          </p>
          <button className="btn-pill-secondary mt-3" onClick={() => blocker.reset()}>
            Stay here
          </button>
          {!busy && (
            <button className="btn-pill-ghost mt-3" onClick={() => blocker.proceed()}>
              Leave and check later
            </button>
          )}
        </div>
      )}
      {items.map((note) => (
        <article
          key={note.id}
          className="flex items-start gap-3 rounded-3xl border border-hairline bg-canvas-pure p-5"
        >
          <label className="grid h-11 w-11 shrink-0 place-items-center">
            <input
              className="h-5 w-5"
              type="checkbox"
              aria-label={`Select ${note.title}`}
              checked={selected.includes(note.id)}
              disabled={!note.available || busy || pending}
              onChange={() => toggle(note.id)}
            />
          </label>
          <button
            className="min-w-0 flex-1 text-left"
            onClick={() => onOpen(note.id)}
            disabled={busy}
          >
            <span className="break-words font-extrabold text-ink">{note.title}</span>
            <span className="mt-2 block text-sm text-slate2">
              {subjects.find((s) => s.subject_key === note.subject_key)?.display_name ??
                'No subject'}{' '}
              ·{' '}
              {terms.find((t) => t.id === note.term_id)?.label ??
                (note.term_id ? 'Saved term' : 'Unfiled')}
            </span>
            <span className="mt-1 block text-sm text-slate2">
              {note.pages.length} photos{note.topic ? ` · ${note.topic}` : ''}
              {note.class_date ? ` · ${note.class_date}` : ''}
            </span>
            <span className="mt-2 block line-clamp-2 text-sm text-ink-soft">
              {note.body || 'Open to read and add your own words.'}
            </span>
          </button>
        </article>
      ))}
    </section>
  );
}
